import { API, buildQueryParams, downloadFile, get, post, put } from "api";
import {
    NewMaterialThirdPartyValues,
    ThirdPartyUpdateValues,
} from "validation/components/ThirdPartyTag/thirdPartyTagRules";
import { CreativesUploadViewModel } from "./factories/creatives.factory";
import { OK } from "http-status-codes";

export type MaterialSpecActionType = "APPROVE" | "DELIVER";

export type DownloadType = "AD_MATERIAL" | "MATERIAL_SPEC";

export interface AdMaterialReq {
    currentMaterialSpecificationId: number;
    adMaterialId: number;
}

export interface MaterialSpecHeaders {
    header: string[];
    rows: MaterialHeaderRow[];
}

export interface MaterialHeaderRow {
    id: number;
    values: Array<{ value: string; colour?: string; tooltip?: string }>;
}

export interface MaterialUpload {
    admaterial: AdMaterial;
    matspecid: number;
}

export interface AppConfig {
    lineItemHeader: MaterialSpecHeaders;
    mimeTypes: string[];
    showSgCode: boolean;
    // eslint-disable-next-line
    [key: string]: any;
}

export interface CreativeUploads {
    matspecs: MaterialSpecification[];
    ad: AdDetails;
    config: AppConfig;
    user: string; // TODO: temporary, JWT is not always returning username
}

export interface MaterialSpecification {
    id: number;
    name: string;
    width: string;
    height: string;
    size: string;
    admaterial: AdMaterial[];
    printed: boolean;
    caption: string;
    status: Status;
    firstinsertiondate: string;
    type: {
        name: string;
        titles: string[];
    };
    permissions: Permission[];
    customernote: string;
    creativetype: string;
    adserverurl: string;
}

interface AdDetails {
    id: number;
    orderno: string;
}

export interface Permission {
    name: string;
    enabled: boolean;
    display?: boolean;
    reason?: string;
}

export interface AdMaterial {
    actions: { [key: string]: string };
    bookedsizes: string;
    version?: number;
    inputfilename: string;
    archived: boolean;
    assigned: boolean;
    creationdate: string;
    deleted: boolean;
    description?: string;
    format: string;
    haserrors?: Array<number>;
    id: number;
    externalid: string;
    printed: boolean;
    modificationdate: string;
    original?: AdMaterialDetails[];
    preview?: AdMaterialDetails;
    bigpreview?: AdMaterialDetails;
    previewsArray: AdMaterialDetails[];
    status: Status;
    size: string | string[];
    totalfilesize: string;
    processedheight: number;
    processedwidth: number;
    sgcode: number;
    pixeltracker: string;
    tag: string;
    url: string;
    urls: string[];
    permissions: Permission[];
}

export interface AdMaterialDetails {
    format: string;
    height: string;
    id: number;
    serverfilename: string;
    size: string;
    type: string;
    url: string;
    width: string;
}

export interface Status {
    code: number;
    color: string;
    text: string;
}

export interface PreflightReport {
    errors: PreflightReportInfo[];
    warnings: PreflightReportInfo[];
    info: PreflightReportInfo[];
}

export interface PrintPreflightReport {
    haserrors: boolean;
    xmllogUrl: string;
    htmllogUrl: string;
    pdflogUrl: string;
}

export interface PreflightReportInfo {
    key: string;
    value: string;
    unit: string;
    expectedValue: string;
}

export interface ThirdPartyUploadResponse {
    admaterial: AdMaterial;
    matspec_status: number;
    print: boolean;
}

export interface MatSpecsNewThirdPartyMaterial extends CreativesUploadViewModel {
    adMaterialId: number;
}

export interface DeletedMaterialRes {
    deleted: boolean;
    admaterial?: AdMaterial;
}

export interface SourceStatusRes {
    name: string;
    id: number;
    profile?: string;
}

export interface AuditStatusRes {
    name?: string;
    type?: string;
    value?: string;
    file?: string;
}

export interface AuditRes {
    source: string | SourceStatusRes;
    id: number;
    user: UserRes;
    status: Status;
    source_enum: string;
    audits: AuditStatusRes[];
    endtime: string;
}

export interface CreativeHistoryRes {
    totalSize: number;
    audits: AuditRes[];
}

export interface UserRes {
    name: string;
    id: number;
    rol: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
}

export interface PrintMaterialDescription {
    materialSpecificationId: number;
    description?: string;
}

export interface PrintMaterialAdServerProperties extends PrintMaterialDescription {
    adMaterialId: number;
    url: string;
}

export const getBookingsReq = async (headers: HeadersInit): Promise<CreativeUploads | undefined> => {
    const { parsedBody } = await get<CreativeUploads>(`${API.baseApiUrl}/loadcampaign`, {
        headers,
    });

    return parsedBody;
};

export const uploadMaterialsReq = async (headers: HeadersInit, materials: FormData) => {
    const { parsedBody } = await post<MaterialUpload[]>(
        `${API.baseApiUrl}/upload`,
        materials,
        {
            headers,
        },
        false
    );

    return parsedBody || [];
};

export const getAdMaterialReq = async (headers: HeadersInit, materialId: number) => {
    const { parsedBody } = await get<AdMaterial>(`${API.baseApiUrl}/loadadmaterial?admaterial=${materialId}`, {
        headers,
    });

    return parsedBody;
};

export const uploadThirdPartyTagReq = async (
    headers: HeadersInit,
    matspecId: string,
    adMatId: number | null,
    tag?: string,
    sgCode?: string
) => {
    let encodedTag = tag || sgCode;
    if (encodedTag) {
        encodedTag = btoa(unescape(encodeURIComponent(encodedTag)));
    }

    const queryParams = buildQueryParams({
        content: encodedTag,
        sgcode: sgCode,
        matspec: matspecId,
        admaterial: adMatId,
    });

    return await post<ThirdPartyUploadResponse[]>(`${API.baseApiUrl}/upload3partytag?${queryParams}`, null, {
        headers,
    });
};

export const uploadThirdPartyInfoReq = async (
    headers: HeadersInit,
    { adMaterialId, pixelTracker, description, url }: ThirdPartyUpdateValues
) => {
    if (description || url || pixelTracker) {
        const queryParams = buildQueryParams({
            description,
            userurl: url,
            admaterial: adMaterialId,
            pixeltracker: pixelTracker,
        });

        await put(`${API.baseApiUrl}/admaterialupdate?${queryParams}`, null, { headers });
    }
};

export const updateThirdPartyTagReq = async (
    headers: HeadersInit,
    {
        materialSpecificationId,
        adMaterialId,
        tag,
        sgCode,
    }: { materialSpecificationId: number; adMaterialId: number; tag?: string; sgCode?: number }
) => {
    const { parsedBody } = await uploadThirdPartyTagReq(
        headers,
        materialSpecificationId.toString(),
        adMaterialId,
        tag,
        sgCode?.toString()
    );

    return parsedBody ? parsedBody[0] : [];
};

export const createThirdPartyInfoReq = async (
    headers: HeadersInit,
    { materialSpecificationId, pixelTracker, description, url, tag, sgCode }: NewMaterialThirdPartyValues
) => {
    const { parsedBody } = await uploadThirdPartyTagReq(headers, materialSpecificationId.toString(), null, tag, sgCode);

    // after creating admaterial with tag, save other info
    if (parsedBody && parsedBody.length > 0) {
        await uploadThirdPartyInfoReq(headers, {
            materialSpecificationId,
            adMaterialId: parsedBody[0].admaterial.id,
            pixelTracker,
            description,
            url,
        });
    }

    return parsedBody;
};

export const getPreflightReportReq = async (headers: HeadersInit, adMaterialId: number) => {
    const { parsedBody } = await get<PreflightReport>(`${API.baseApiUrl}/preflight-report?admaterial=${adMaterialId}`, {
        headers,
    });

    return parsedBody;
};

export const getPrintPreflightReportReq = async (headers: HeadersInit, adMaterialId: number) => {
    const { parsedBody } = await get<PrintPreflightReport>(
        `${API.baseApiUrl}/loadpreflightreport?admaterial=${adMaterialId}`,
        {
            headers,
        }
    );

    return parsedBody;
};

export const removeMaterialReq = async (headers: HeadersInit, adMaterialId: number) => {
    const queryParams = buildQueryParams({ admaterial: adMaterialId });

    const { parsedBody } = await post<DeletedMaterialRes>(`${API.baseApiUrl}/admaterialdelete?${queryParams}`, null, {
        headers,
    });

    return parsedBody;
};

export const updateMaterialSpecStatusReq = async (
    headers: HeadersInit,
    action: MaterialSpecActionType,
    { materialSpecId, status }: { materialSpecId: number; status: number }
) => {
    const queryParams = buildQueryParams({ matspec: materialSpecId, status });

    if (action === "APPROVE") {
        const { parsedBody } = await post<{ matspec: MaterialSpecification }>(
            `${API.baseApiUrl}/matspecapprove?${queryParams}`,
            null,
            {
                headers,
            }
        );

        return parsedBody?.matspec;
    }

    const { parsedBody } = await post<{ matspec: MaterialSpecification }>(
        `${API.baseApiUrl}/matspecdeliver?${queryParams}`,
        null,
        {
            headers,
        }
    );

    return parsedBody?.matspec;
};

export const fetchMaterialSpecById = async (headers: HeadersInit, materialSpecId: number) => {
    const queryParams = buildQueryParams({ matspec: materialSpecId });

    const { parsedBody } = await get<MaterialSpecification>(`${API.baseApiUrl}/loadmatspec?${queryParams}`, {
        headers,
    });

    return parsedBody;
};

export const downloadMaterialsReq = async (headers: HeadersInit, action: DownloadType, { id }: { id: number }) => {
    if (action === "AD_MATERIAL") {
        const queryParams = buildQueryParams({ admaterial: id, asurl: false });
        await downloadFile(`${API.baseApiUrl}/downloadmaterial?${queryParams}`, {
            headers,
        });

        return OK;
    }

    const queryParams = buildQueryParams({ matspec: id, asurl: false });
    await downloadFile(`${API.baseApiUrl}/downloadmatspec?${queryParams}`, {
        headers,
    });

    return OK;
};

export const approveAdMaterialReq = async (headers: HeadersInit, adMaterialId: number) => {
    return await post(`${API.baseApiUrl}/admaterialcorrect?admaterial=${adMaterialId}`, null, { headers });
};

export const getCreativeHistory = async (
    headers: HeadersInit,
    offset: number,
    materialSpecId?: number,
    materialId?: number,
    size = 25
): Promise<CreativeHistoryRes | undefined> => {
    const queryParams = buildQueryParams({
        matspec: materialSpecId,
        admaterial: materialId,
        offset: offset.toString(),
        size,
    });
    const { parsedBody } = await get<CreativeHistoryRes>(`${API.baseApiUrl}/loadaudits?${queryParams}`, { headers });

    return parsedBody;
};

export const getHistoricalVersions = async (headers: HeadersInit, materialSpecId?: number) => {
    const queryParams = buildQueryParams({ matspec: materialSpecId });
    const { parsedBody } = await get<{ versions: AdMaterial[] }>(
        `${API.baseApiUrl}/admaterialversions?${queryParams}`,
        { headers }
    );

    return parsedBody?.versions || [];
};

export const submitPrintMaterialDescriptionReq = async (
    headers: HeadersInit,
    { materialSpecificationId, description }: PrintMaterialDescription
) => {
    await put(`${API.baseApiUrl}/matspecupdate?matspec=${materialSpecificationId}&customernote=${description}`, null, {
        headers,
    });
};

export const submitPrintMaterialAdServerPropertiesReq = async (
    headers: HeadersInit,
    { materialSpecificationId, adMaterialId, description, url }: PrintMaterialAdServerProperties
) => {
    if (description) {
        await submitPrintMaterialDescriptionReq(headers, { materialSpecificationId, description });
    }

    const queryParams = buildQueryParams({
        description,
        userurl: url,
        admaterial: adMaterialId,
    });

    await put(`${API.baseApiUrl}/admaterialupdate?${queryParams}`, null, { headers });
};
